import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload,
  AttachMoney,
  CheckCircle,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { submitTopupRequest } from 'api/wallet';
import { ClientGetStationList, ClientGetLatestExchangeRates } from 'api/client';
import { useTranslation } from 'react-i18next';

const WalletTopup = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    amount: '',
    currency: 'USD',
    creditCurrency: 'THB',
    paymentSource: '',
    stationId: '',
    description: ''
  });
  const [stationList, setStationList] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(null);

  useEffect(() => {
    ClientGetStationList().then((res) => {
      if (res && res.success) setStationList(Array.isArray(res.data) ? res.data : []);
    });
    ClientGetLatestExchangeRates().then((res) => {
      if (res && res.success) setExchangeRate({ USD: Number(res.USD), LAK: Number(res.LAK) });
    });
  }, []);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successDialog, setSuccessDialog] = useState(false);
  const [submittedRequest, setSubmittedRequest] = useState(null);

  const MAX_AMOUNTS = { USD: 1000, THB: 30000, LAK: 20000000 };

  const currencies = [
    { value: 'USD', label: 'US Dollar ($)', symbol: '$' },
    { value: 'THB', label: 'Thai Baht (฿)', symbol: '฿' },
    { value: 'LAK', label: 'Lao Kip (₭)', symbol: '₭' }
  ];

  // Estimate only — the backend converts with the latest rate at submit time.
  const estimatedCredit = () => {
    const amt = parseFloat(formData.amount);
    if (!amt || !exchangeRate || formData.currency === formData.creditCurrency) return null;
    const toTHB = (v, c) => (c === 'USD' ? v * exchangeRate.USD : c === 'LAK' ? v / exchangeRate.LAK : v);
    const fromTHB = (v, c) => (c === 'USD' ? v / exchangeRate.USD : c === 'LAK' ? v * exchangeRate.LAK : v);
    return Math.round(fromTHB(toTHB(amt, formData.currency), formData.creditCurrency) * 100) / 100;
  };
  const symbolOf = (code) => currencies.find(c => c.value === code)?.symbol || '';

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (JPG, PNG, GIF)');
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setError('');

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const parsed = parseFloat(formData.amount);
    if (!formData.amount || parsed <= 0) {
      setError('Please enter a valid amount');
      return false;
    }
    const max = MAX_AMOUNTS[formData.currency];
    if (parsed > max) {
      setError(`Maximum amount for ${formData.currency} is ${max.toLocaleString()} per request`);
      return false;
    }
    if (!formData.currency) {
      setError('Please select a currency');
      return false;
    }
    if (!formData.paymentSource) {
      setError('Please select a payment source');
      return false;
    }
    if (formData.paymentSource === 'cash_store' && !formData.stationId) {
      setError('Please select the branch where you paid cash');
      return false;
    }
    if (!selectedFile) {
      setError('Please select an evidence photo');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Create form data for file upload
      const uploadFormData = new FormData();
      uploadFormData.append('amount', formData.amount);
      uploadFormData.append('currency', formData.currency);
      uploadFormData.append('creditCurrency', formData.creditCurrency);
      uploadFormData.append('paymentSource', formData.paymentSource);
      if (formData.paymentSource === 'cash_store') uploadFormData.append('stationId', formData.stationId);
      uploadFormData.append('description', formData.description);
      uploadFormData.append('evidencePhoto', selectedFile);

      const result = await submitTopupRequest(uploadFormData);

      setSubmittedRequest(result.data);
      setSuccessDialog(true);

      // Reset form
      setFormData({ amount: '', currency: 'USD', creditCurrency: 'THB', paymentSource: '', stationId: '', description: '' });
      setSelectedFile(null);
      setFilePreview(null);
      
    } catch (err) {
      setError(err.message || 'Failed to submit top-up request');
      console.error('Error submitting top-up request:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentSymbol = () => {
    const currency = currencies.find(c => c.value === formData.currency);
    return currency ? currency.symbol : '$';
  };

  return (
    <Box m={2}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/customer-portal/wallet')}
          sx={{ mr: 2 }}
        >
          Back to Wallet
        </Button>
        <Typography variant="h4">
          Top Up Wallet
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Top-up Form */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
              <AttachMoney sx={{ mr: 1 }} />
              Top-up Request Form
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Amount and Currency */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={formData.amount}
                    onChange={handleInputChange('amount')}
                    required
                    inputProps={{
                      min: formData.currency === 'LAK' ? 1 : 0.01,
                      step: formData.currency === 'LAK' ? 1 : 0.01,
                      max: MAX_AMOUNTS[formData.currency],
                    }}
                    helperText={`Max: ${MAX_AMOUNTS[formData.currency]?.toLocaleString()} ${formData.currency}`}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {getCurrentSymbol()}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={formData.currency}
                      onChange={handleInputChange('currency')}
                      label="Currency"
                    >
                      {currencies.map((currency) => (
                        <MenuItem key={currency.value} value={currency.value}>
                          {currency.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Credit currency — defaults to THB, so USD/LAK are auto-converted */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>{t('wallet.creditAs')}</InputLabel>
                    <Select
                      value={formData.creditCurrency}
                      onChange={handleInputChange('creditCurrency')}
                      label={t('wallet.creditAs')}
                    >
                      {currencies.map((currency) => (
                        <MenuItem key={currency.value} value={currency.value}>
                          {currency.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {estimatedCredit() !== null && (
                    <Typography variant="body2" color="secondary" sx={{ mt: 1 }}>
                      {t('wallet.estimatedCredit', {
                        amount: `${symbolOf(formData.creditCurrency)}${estimatedCredit().toLocaleString()} ${formData.creditCurrency}`,
                      })}
                    </Typography>
                  )}
                </Grid>

                {/* Payment Source */}
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Payment Source *</InputLabel>
                    <Select
                      value={formData.paymentSource}
                      onChange={handleInputChange('paymentSource')}
                      label="Payment Source *"
                    >
                      <MenuItem value="bank_transfer">{t('wallet.admin.paymentSourceBankTransfer')}</MenuItem>
                      <MenuItem value="cash_store">{t('wallet.admin.paymentSourceCashStore')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Station (only when paying cash at a branch) */}
                {formData.paymentSource === 'cash_store' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>{t('wallet.admin.selectStation')} *</InputLabel>
                      <Select
                        value={formData.stationId}
                        onChange={handleInputChange('stationId')}
                        label={`${t('wallet.admin.selectStation')} *`}
                      >
                        {stationList.map((st) => (
                          <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* Description */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description (Optional)"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={handleInputChange('description')}
                    placeholder="Add any notes about this top-up request..."
                    helperText="You can add details about the payment source or any other relevant information"
                  />
                </Grid>

                {/* File Upload */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Upload Evidence Photo
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please upload a clear photo of your bank transfer receipt, payment screenshot, or other payment evidence.
                  </Typography>
                  
                  <Button
                    component="label"
                    variant="outlined"
                    color="secondary"
                    startIcon={<CloudUpload />}
                    size="large"
                    sx={{ mb: 2 }}
                  >
                    {selectedFile ? 'Change Photo' : 'Select Photo'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleFileSelect}
                    />
                  </Button>

                  {selectedFile && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Selected file: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </Typography>
                    </Box>
                  )}
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    size="large"
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    {loading ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        Submitting...
                      </>
                    ) : (
                      'Submit Top-up Request'
                    )}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        {/* Preview Panel */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Request Preview
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Amount
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {formData.amount ? `${getCurrentSymbol()}${parseFloat(formData.amount).toLocaleString()}` : 'Not specified'}
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Currency
                </Typography>
                <Typography variant="body1">
                  {currencies.find(c => c.value === formData.currency)?.label || 'USD'}
                </Typography>
              </CardContent>
            </Card>

            {formData.description && (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {formData.description}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {filePreview && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Evidence Photo Preview
                  </Typography>
                  <Box
                    component="img"
                    src={filePreview}
                    alt="Evidence preview"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: 200,
                      objectFit: 'contain',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Success Dialog */}
      <Dialog
        open={successDialog}
        onClose={() => setSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <CheckCircle color="success" sx={{ mr: 1 }} />
          Top-up Request Submitted Successfully
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your wallet top-up request has been submitted and is now pending admin approval.
          </Typography>
          {submittedRequest && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Request ID: {submittedRequest._id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Amount: {symbolOf(submittedRequest.currency)}{submittedRequest.amount} {submittedRequest.currency}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status: Pending Review
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/customer-portal/wallet/requests')} variant="outlined" color="secondary">
            View My Requests
          </Button>
          <Button
            onClick={() => navigate('/customer-portal/wallet')}
            variant="contained"
            color="secondary"
          >
            Back to Wallet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WalletTopup;